import { hotEvents } from "@/mocks/events";
import StatusTag from "./StatusTag";

function scrollToId(href: string) {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Hero() {
  const focus = hotEvents.find((e) => e.featured) ?? hotEvents[0];

  return (
    <section id="top" className="relative overflow-hidden">
      <div className="pointer-events-none absolute -right-40 -top-40 h-96 w-96 rounded-full bg-accent-100/70 blur-3xl" />
      <div className="pointer-events-none absolute -left-32 top-40 h-80 w-80 rounded-full bg-primary-100/70 blur-3xl" />

      <div className="relative mx-auto w-full max-w-7xl px-4 pb-10 pt-8 md:px-6 md:pt-14 lg:pt-16">
        <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
          <div>
            <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-3.5 py-1.5 text-xs font-semibold text-primary-700">
              <i className="ri-fire-fill text-sm" />
              全网吃瓜 · 事件时间线整理平台
            </span>

            <h1 className="mt-6 font-heading text-3xl font-black leading-[1.18] text-foreground-950 sm:text-4xl lg:text-[44px]">
              把复杂瓜事
              <br />
              捋成一条<span className="text-primary-600">清晰时间线</span>
            </h1>

            <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
              从起因、经过到发酵与结果，我们把散落在各平台的信息做客观交叉整理，标注「已确认、双方说法、尚未证实」，让你不用自己东拼西凑，直接看懂每起热点事件的完整前因后果。
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => scrollToId("#hot")}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-fire-line text-lg" />
                去看今日热榜
              </button>
              <button
                type="button"
                onClick={() => scrollToId("#about")}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                了解整理原则
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>

          {/* focus card */}
          <article className="relative overflow-hidden rounded-2xl border border-background-200 bg-background-50 p-5 shadow-hairline md:p-6">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-accent-700">
                <i className="ri-star-smile-fill text-sm" />
                本期聚焦
              </span>
              <span className="rounded-full bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-900">
                {focus.category}
              </span>
            </div>

            <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-900 md:text-2xl">
              {focus.title}
            </h3>
            <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground-600">{focus.summary}</p>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <StatusTag status={focus.status} />
              <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-100 px-2.5 py-1 text-xs font-medium text-foreground-600">
                <i className="ri-time-line" />
                更新于 {focus.updated}
              </span>
            </div>

            <div className="mt-5">
              <div className="flex items-center justify-between text-xs font-medium text-foreground-600">
                <span>热度走势</span>
                <span className="font-semibold text-primary-600">{focus.heat}</span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-background-200">
                <div className="h-full w-[78%] rounded-full bg-gradient-to-r from-accent-400 to-primary-500" style={{ width: `${focus.heat}%` }} />
              </div>
            </div>

            <button
              type="button"
              onClick={() => scrollToId("#deep")}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
            >
              查看完整时间线
              <i className="ri-arrow-right-line" />
            </button>
          </article>
        </div>

        {/* stats */}
        <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[
            { icon: "ri-stack-line", num: "128+", label: "累计整理事件", note: "含已收束与发酵中" },
            { icon: "ri-apps-2-line", num: "6", label: "覆盖内容领域", note: "娱乐·科技·民生等频道" },
            { icon: "ri-flashlight-fill", num: "2h", label: "平均补更速度", note: "热点新进展快速跟进" },
          ].map((s, i) => (
            <div
              key={s.label}
              className={`flex items-center gap-4 rounded-2xl border border-background-200 bg-background-50 px-5 py-4 ${
                i === 1 ? "bg-accent-100/70" : ""
              }`}
            >
              <span
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-xl ${
                  i === 1 ? "bg-accent-500 text-background-50" : "bg-primary-100 text-primary-700"
                }`}
              >
                <i className={s.icon} />
              </span>
              <div className="min-w-0">
                <div className="font-heading text-2xl font-bold text-foreground-900">{s.num}</div>
                <p className="text-xs font-medium text-foreground-700">{s.label}</p>
                <p className="mt-0.5 text-xs text-foreground-500">{s.note}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}